import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { api, till } from "@/lib/api";
import { money } from "@/lib/format";
import type { Item } from "@/lib/types";
import { Button, Field, Input, Pager, Select, cx, usePaged, useToast } from "./ui";
import { Card, PageHead } from "./Layout";

type Shop = { id: string; name: string };
type Saved = { shopId: string; category: string; counts: Record<string, string> };
const KEY = "stock-count-plain";
const read = (): Saved | null => {
  try {
    // Something saved by an older version, or in the wrong shape, is thrown away rather than trusted.
    const d = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<Saved> | null;
    if (!d || typeof d !== "object" || !d.counts || typeof d.counts !== "object" || Array.isArray(d.counts)) return null;
    const counts = Object.fromEntries(Object.entries(d.counts).filter(([k, v]) => typeof k === "string" && (typeof v === "string" || typeof v === "number")).map(([k, v]) => [k, String(v)]));
    return { shopId: typeof d.shopId === "string" ? d.shopId : "", category: typeof d.category === "string" ? d.category : "", counts };
  } catch {
    return null;
  }
};
type Tab = "todo" | "done" | "diff";

/**
 * Count stock (Buildwise): choose what you're counting, then count. The count is blind: a product's
 * difference only shows after you type its number. Kept on this device until saved.
 */
export function PlainCount() {
  const toast = useToast();
  const qc = useQueryClient();
  const itemsQ = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: Item[] }>(till("/admin/items")) });
  const shopsQ = useQuery({ queryKey: ["shops"], queryFn: () => api<{ shops: Shop[] }>(till("/admin/shops")) });
  const shops = shopsQ.data?.shops ?? [];
  const saved = useMemo(read, []);
  const [step, setStep] = useState<"start" | "count" | "review">(saved && Object.keys(saved.counts).length ? "count" : "start");
  const [picked, setPicked] = useState(saved?.shopId ?? "");
  const shopId = picked || shops[0]?.id || "main";
  const [category, setCategory] = useState(saved?.category ?? "");
  const [scope, setScope] = useState<"all" | "category">(saved?.category ? "category" : "all");
  const [counts, setCounts] = useState<Record<string, string>>(saved?.counts ?? {});
  const [tab, setTab] = useState<Tab>("todo");
  const [frozen, setFrozen] = useState<string[] | null>(null);
  const [find, setFind] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // A count saved on this device can name products or a shop that have since been deleted: drop those.
  useEffect(() => {
    const all = itemsQ.data?.items;
    if (!all || !shopsQ.data) return;
    const known = new Set(all.map((i) => i.id));
    setCounts((c) => {
      const kept = Object.fromEntries(Object.entries(c).filter(([k]) => known.has(k)));
      return Object.keys(kept).length === Object.keys(c).length ? c : kept;
    });
    if (picked && !shopsQ.data.shops.some((s) => s.id === picked)) setPicked("");
  }, [itemsQ.data, shopsQ.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const inShop = useMemo(() => (itemsQ.data?.items ?? []).filter((i) => i.active && i.tracking === "qty" && (i.location ?? "main") === shopId), [itemsQ.data, shopId]);
  const categories = useMemo(() => [...new Set(inShop.map((i) => i.category).filter(Boolean))].sort(), [inShop]);
  const pool = useMemo(() => inShop.filter((i) => scope === "all" || i.category === category), [inShop, scope, category]);
  const entered = (i: Item) => counts[i.id] !== undefined && counts[i.id] !== "";
  const done = pool.filter(entered);
  const diffs = done.filter((i) => Number(counts[i.id]) !== (i.onHand ?? 0));
  const value = diffs.reduce((n, i) => n + (Number(counts[i.id]) - (i.onHand ?? 0)) * (i.cost ?? 0), 0);

  useEffect(() => {
    try {
      if (Object.keys(counts).length) localStorage.setItem(KEY, JSON.stringify({ shopId, category: scope === "category" ? category : "", counts }));
      else localStorage.removeItem(KEY);
    } catch {
      /* it still works, it just won't survive leaving the page */
    }
  }, [counts, shopId, scope, category]);

  // A row stays put while you type, and moves to its new tab when you next change tabs.
  const inTab = (t: Tab, i: Item) => (t === "todo" ? !entered(i) : t === "done" ? entered(i) : entered(i) && Number(counts[i.id]) !== (i.onHand ?? 0));
  const openTab = (t: Tab) => {
    setTab(t);
    setFrozen(pool.filter((i) => inTab(t, i)).map((i) => i.id));
  };
  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const list = (frozen ? pool.filter((i) => frozen.includes(i.id)) : pool.filter((i) => inTab(tab, i))).filter((i) => words.every((w) => i.name.toLowerCase().includes(w)));
  const paged = usePaged(list);
  const start = () => {
    setStep("count");
    setFind("");
    setTab("todo");
    setFrozen(pool.filter((i) => !entered(i)).map((i) => i.id));
  };
  const next = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const all = [...document.querySelectorAll<HTMLInputElement>("input[data-count]")];
    all[all.indexOf(e.currentTarget) + 1]?.focus();
  };
  const save = async () => {
    setBusy(true);
    try {
      const r = await api<{ changed: number; same: number }>(till("/admin/stock-count"), { body: { note, lines: done.map((i) => ({ itemId: i.id, counted: Number(counts[i.id]) })) } });
      toast(`Count saved. ${r.changed} updated, ${r.same} already right`);
      setCounts({});
      setNote("");
      setStep("start");
      void qc.invalidateQueries({ queryKey: ["admin-items"] });
      void qc.invalidateQueries({ queryKey: ["moves"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save the count", "err");
    } finally {
      setBusy(false);
    }
  };
  const shopName = shops.find((s) => s.id === shopId)?.name;
  const scopeText = [shopName, scope === "all" ? "Everything" : category].filter(Boolean).join(" · ");
  const bar = "sticky bottom-[88px] z-10 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3 shadow-[0_-6px_24px_rgba(11,21,23,0.1)] ring-1 ring-slate-200 md:bottom-4";

  if (step === "start") {
    const choice = (on: boolean) => cx("flex min-h-[62px] w-full items-center gap-3 rounded-[14px] border-[1.5px] px-3.5 py-2.5 text-left", on ? "border-brand bg-brand-tint" : "border-slate-200 bg-white");
    const dot = (on: boolean) => cx("h-5 w-5 flex-none rounded-full", on ? "border-[6px] border-brand" : "border-2 border-slate-400");
    return (
      <>
        <PageHead title="Count stock" sub="Check what's really on the shelf against the records." />
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Card>
          <div className="flex flex-col gap-3 p-4 sm:p-5">
            <h2 className="text-lg font-extrabold">What are you counting?</h2>
            {shops.length > 1 ? <Field label="Shop"><Select value={shopId} onChange={(e) => { setPicked(e.target.value); setCategory(""); setCounts({}); }}>{shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field> : null}
            {itemsQ.data && !inShop.length ? (
              <div className="rounded-[14px] border border-dashed border-slate-300 px-4 py-6 text-center">
                <b className="block text-[15px]">Nothing to count yet</b>
                <span className="mt-1 block text-[13px] text-slate-500">{shopName ? `${shopName} has` : "This shop has"} no products that keep stock. Add products first, then come back to count them.</span>
                <span className="mt-3 flex flex-wrap justify-center gap-2"><Link to="/products" className="inline-flex h-10 items-center rounded-xl bg-brand px-4 text-sm font-bold text-white">Add a product</Link><Link to="/stock" className="inline-flex h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold">Receive stock</Link></span>
              </div>
            ) : <>
            <div className="grid gap-3 sm:grid-cols-2">
            <button type="button" aria-pressed={scope === "all"} onClick={() => setScope("all")} className={choice(scope === "all")}><span className={dot(scope === "all")} /><span><b className="block text-[15px]">Everything</b><span className="text-[13px] text-slate-500">{inShop.length} product{inShop.length === 1 ? "" : "s"}</span></span></button>
            <button type="button" disabled={!categories.length} aria-pressed={scope === "category"} onClick={() => { setScope("category"); if (!category) setCategory(categories[0] ?? ""); }} className={cx(choice(scope === "category"), !categories.length && "cursor-not-allowed opacity-50")}><span className={dot(scope === "category")} /><span><b className="block text-[15px]">One category</b><span className="text-[13px] text-slate-500">{categories.length ? "Good for a quick check" : "No categories yet"}</span></span></button>
            </div>
            {scope === "category" ? <Field label="Category"><Select value={category} onChange={(e) => setCategory(e.target.value)}>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</Select></Field> : null}
            <Button tone="primary" disabled={!pool.length} onClick={start}><span>{Object.keys(counts).length ? "Carry on counting" : "Start counting"}</span></Button>
            </>}
          </div>
        </Card>
        <Card title="How counting works">
          <ol className="flex flex-col gap-3.5 px-5 pb-5 text-sm">
            {[["Choose", "Everything in the shop, or one category for a quick check."], ["Count", "Type what you see on the shelf. The system's number stays hidden, so you count what's really there."], ["Review and save", "See only what's different and its value, then save. The stock is corrected and the change goes in the Stock log."]].map(([t, d], n) => (
              <li key={t} className="flex gap-3"><span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-brand-tint text-[13px] font-extrabold text-brand-ink">{n + 1}</span><span><b className="block font-semibold">{t}</b><span className="text-[13px] text-slate-500">{d}</span></span></li>
            ))}
            <li className="rounded-xl bg-slate-50 px-3 py-2.5 text-[13px] text-slate-500">Your count is kept on this device if you leave, so you can carry on later.</li>
          </ol>
        </Card>
        </div>
      </>
    );
  }

  if (step === "review") {
    return (
      <>
        <PageHead title="Save this count?" sub={`${done.length} counted · ${done.length - diffs.length} match · ${diffs.length} different`}><Button onClick={() => { setStep("count"); openTab(tab); }}>Keep counting</Button></PageHead>
        <Card>
          {diffs.map((i) => {
            const d = Number(counts[i.id]) - (i.onHand ?? 0);
            return <div key={i.id} className="flex min-h-14 items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5 text-sm last:border-b-0"><span className="min-w-0"><b className="block font-semibold">{i.name}</b><span className="text-xs text-slate-500">Records {(i.onHand ?? 0).toLocaleString("en-GB")}, counted {Number(counts[i.id]).toLocaleString("en-GB")}</span></span><b className="flex-none">{d > 0 ? "+" : ""}{d.toLocaleString("en-GB")}</b></div>;
          })}
          {!diffs.length ? <p className="px-4 py-8 text-center text-sm text-slate-500">Everything you counted matches the records.</p> : null}
          <div className="p-4"><Field label="Note (optional)"><Input value={note} maxLength={160} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Month-end count" /></Field></div>
        </Card>
        <div className={bar}>
          <span className="text-sm"><b className="text-base">{value < 0 ? "− " : ""}{money(Math.abs(value))}</b> <span className="text-slate-500">value of the differences, at cost</span></span>
          <Button tone="primary" disabled={busy} className="max-md:w-full" onClick={() => void save()}>{busy ? "Saving…" : "Save the count"}</Button>
        </div>
        <p className="mt-2 text-xs text-slate-500">Saving sets stock to your counted numbers and adds each change to the stock log. Anything sold since you counted a product isn't allowed for, so count when the shop is quiet.</p>
      </>
    );
  }

  const tabs = [["todo", "To count", pool.length - done.length], ["done", "Counted", done.length], ["diff", "Different", diffs.length]] as const;
  return (
    <>
      <PageHead title="Count stock" sub={scopeText}><Button onClick={() => setStep("start")}>Change what I'm counting</Button></PageHead>
      <div role="tablist" aria-label="Count" className="mb-3 flex gap-1 overflow-x-auto rounded-[14px] bg-slate-200/70 p-1">
        {tabs.map(([k, l, n]) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => openTab(k)} className={cx("min-h-11 flex-1 whitespace-nowrap rounded-[11px] px-3 text-[13px] font-bold", tab === k ? "bg-white shadow-sm" : "text-slate-600")}>{l} {n}</button>)}
      </div>
      <Input aria-label="Find a product" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a product" className="mb-3" />
      <Card>
        {paged.rows.map((i) => {
          const has = entered(i);
          const d = has ? Number(counts[i.id]) - (i.onHand ?? 0) : 0;
          return (
            <div key={i.id} className="grid grid-cols-[minmax(0,1fr)_112px] items-center gap-x-3 gap-y-1 border-b border-slate-100 px-4 py-2.5 last:border-b-0 md:grid-cols-[minmax(0,1fr)_150px_120px]">
              <span className="min-w-0"><b className="block text-sm font-semibold">{i.name}</b><span className="text-xs text-slate-500">{[i.category, i.unit ? `by the ${i.unit}` : ""].filter(Boolean).join(" · ")}</span></span>
              <input data-count aria-label={`Counted for ${i.name}`} inputMode="numeric" enterKeyHint="next" value={counts[i.id] ?? ""} onKeyDown={next} onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setCounts((c) => { const n = { ...c }; if (v === "") delete n[i.id]; else n[i.id] = v; return n; }); }} className="h-11 w-full rounded-[11px] border-[1.5px] border-transparent bg-slate-100 px-3 text-base font-semibold outline-none focus:border-brand focus:bg-white" />
              <span className={cx("text-right text-[13px] font-semibold max-md:col-span-2", !has && "max-md:hidden", d === 0 ? "text-brand-ink" : "text-warn-ink")}>{!has ? "" : d === 0 ? "Matches" : d > 0 ? `${d.toLocaleString("en-GB")} over` : `${Math.abs(d).toLocaleString("en-GB")} short`}</span>
            </div>
          );
        })}
        {!list.length ? <p className="px-4 py-8 text-center text-sm text-slate-500">{find ? "No product matches." : tab === "todo" ? "Everything here is counted." : tab === "done" ? "Nothing counted yet." : "No differences so far."}</p> : null}
        <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
      </Card>
      <div className={bar}>
        <span className="text-sm"><b className="text-base">{done.length} of {pool.length} counted</b> <span className="text-slate-500">· {diffs.length} different</span></span>
        <span className="flex gap-2 max-md:w-full">{done.length ? <Button className="max-md:flex-1" onClick={() => { setCounts({}); openTab("todo"); }}>Clear count</Button> : null}<Button tone="primary" className="max-md:flex-1" disabled={!done.length} onClick={() => setStep("review")}>Review and save</Button></span>
      </div>
    </>
  );
}
